import { Component, ChangeDetectorRef, OnDestroy, Inject } from '@angular/core';
import { ActivatedRoute, Params } from '@angular/router';
import { FilterFieldValue } from '../../covance-filter';
import { UrlBuilder } from '../../core/url-builder/url-builder';
import { Site } from '../../sites/site';
import { Subscription, Observable } from 'rxjs';
import { map, filter, withLatestFrom, takeWhile } from 'rxjs/operators';
import { findIndex } from 'lodash';
import { FilteredLabTestResultsStorage, SITE_LAB_RESULTS_FILTERS_STORAGE } from './filtered-lab-test-results-storage';
import { Settings } from '../../core/settings';
import { FilterStorage, InvestigatorLabTestResultsFilterStorage } from './lab-test-results-filter-storage';
import { SITE_LAB_RESULTS_STORAGE } from './site-test-data-storage';
import { routeData } from '../../shared/route-helpers';
import { LOAD_ALL_RESULTS_QUERY_PARAMETER } from './standart-test-result-query-parameters';

@Component({
    selector: 'on-screen-results',
    template: require('./standard-test-results-main.component.html'),
    styles: [require('./standard-test-results-main.component.scss')],
    providers: [
        { provide: SITE_LAB_RESULTS_FILTERS_STORAGE, useClass: InvestigatorLabTestResultsFilterStorage },
        { provide: SITE_LAB_RESULTS_STORAGE, useClass: FilteredLabTestResultsStorage },
    ]
})
export class StandardTestResultsMainComponent implements OnDestroy {

    public tabs: TabInfo[] = [
        { title: 'Test Results', url: 'test-results' },
        { title: 'Trends', url: 'trends' }
    ];

    public siteId: number;
    public filterGroup = 'standard-test-results';
    public fieldVals: FilterFieldValue[];
    public tabIndex: number;
    public selectedSite: Site;
    public maxNumOfSubjects: number;
    public showLargeSiteWarning: Observable<boolean>;
    private siteSubscription: Subscription;
    private tabSubscription: Subscription;

    constructor(
        route: ActivatedRoute,
        private urlBuilder: UrlBuilder,
        private changeDetectorRef: ChangeDetectorRef,
        @Inject(SITE_LAB_RESULTS_FILTERS_STORAGE) filterStorage: FilterStorage,
        settings: Settings
    ) {
        if (settings.isFeatureEnabled('investigatorBoxplot')) {
            this.tabs.push({ title: 'Box plots', url: 'boxplots' });
        }

        this.fieldVals = filterStorage.filterFieldsData;

        const tabIndexObs = route.params
            .pipe(
                map(p => p['tabName']),
                map(t => findIndex(this.tabs, (p: TabInfo) => p.url === t) || 0)
            );

        this.tabSubscription = tabIndexObs.subscribe(idx => {
            this.tabIndex = idx;
            setTimeout(() => this.changeDetectorRef.markForCheck());
        });

        const site = route.parent.parent.data
            .pipe(
                map(x => x[routeData.selectedSite] as Site),
                filter((x: Site) => !!x)
            );

        this.showLargeSiteWarning =
            route.queryParams
                .pipe(
                    map((p) => !!p[LOAD_ALL_RESULTS_QUERY_PARAMETER]),
                    withLatestFrom(site),
                    map(([v, s]) => s.isExtremelyLarge && !v),
                    takeWhile(r => r, true)
                );

        this.siteSubscription = site.subscribe(s => {
            this.selectedSite = s;
            this.siteId = s.siteId;
        });
    }

    public ngOnDestroy() {
        this.siteSubscription.unsubscribe();
        this.tabSubscription.unsubscribe();
    }

    public tabChanged(event: { index: number }) {
        this.urlBuilder
            .esa()
            .site(this.selectedSite)
            .onScreenResults()
            .standardTestResults(this.tabs[event.index].url)
            .navigate({ preserveFragments: true, preserveQueryParams: true });
    }

    public toggleLoadAll() {
        const queryParams: Params = {};
        queryParams[LOAD_ALL_RESULTS_QUERY_PARAMETER] = true;
        this.urlBuilder
            .esa()
            .site(this.selectedSite)
            .onScreenResults()
            .standardTestResults(this.tabs[0].url)
            .navigate({ queryParams: queryParams, navigationExtras: { queryParamsHandling: 'merge' } });
    }
}

interface TabInfo {
    title: string;
    url: string;
}